// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * main.ts — bootstrap and the screen state machine.
 *
 * The one rule that outranks the rest lives here: the room is joined ONCE and
 * held until the player deliberately goes back to the menu. A rematch is a vote
 * plus a new round number inside the living room — never a leave and rejoin,
 * which hands you back a room that is already tearing down and leaves every peer
 * alone and hosting itself.
 */

import './styles/mobile.css';
import './styles/main.css';

import { hardenViewport } from '@ben-gy/game-engine/mobile';
import { createSfx } from '@ben-gy/game-engine/sound';
import { createStore } from '@ben-gy/game-engine/storage';
import { newSeed } from '@ben-gy/game-engine/rng';
import { resolveName } from '@ben-gy/game-engine/identity';
import { createNet, roomAppId, setTurnConfig, type Net, type PeerId } from '@ben-gy/game-engine/net';
import { getTurnConfig } from '@ben-gy/game-engine/turn';
import { createRounds, type RoundInfo, type Rounds } from '@ben-gy/game-engine/rematch';
import {
  clearRoomInUrl, createLobby, createRoomEntry, mintCode, normalizeRoomCode, setRoomInUrl,
} from '@ben-gy/game-engine/lobby';

import { DEFAULT_MODE, MODES, modeOf, type Mode } from './modes';
import { createRunner, type PlayerResult, type Runner } from './runner';
import { createSession, hostModeId, roundOptsOf, type Session } from './session';
import {
  ABOUT_HTML, HOW_TO_HTML, benchmarkFor, clearShareParams, dailySeed,
  openModal, readShareParams, renderMenu, renderResults, shareLink,
} from './ui';
import { runCountdown, type Countdown } from './countdown';
import { el, setHidden } from './dom';

const SLUG = 'kilter';
const MAX_PLAYERS = 6;

const store = createStore(SLUG);
const sfx = createSfx(store.get('muted', false));
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// ------------------------------------------------------------------ shell ---
const app = document.getElementById('app')!;
const mainEl = el('main', 'main-content');
const stage = el('div', 'stage');
mainEl.appendChild(stage);
const footer = el('footer', 'site-footer');
footer.innerHTML = 'Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a> · <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more games, tools &amp; sites</a>';
app.append(mainEl, footer);

hardenViewport();

// ------------------------------------------------------------------ state ---
let mode: Mode = modeOf(store.get('mode', DEFAULT_MODE.id));
let runner: Runner | null = null;
let countdown: Countdown | null = null;
let net: Net | null = null;
let rounds: Rounds | null = null;
let session: Session | null = null;
let lobbyHandle: { destroy: () => void } | null = null;
let roomCode = '';
let seatedIds: PeerId[] = [];
let roster: { id: PeerId; name: string }[] = [];
let remoteResults = new Map<PeerId, PlayerResult>();
let myResult: PlayerResult | null = null;
let tally: Record<string, number> = {};
let waitTimer: number | null = null;
let deepLinkConsumed = false;
let tallied = false;

type Screen = 'menu' | 'entry' | 'lobby' | 'playing' | 'results';
let screen: Screen = 'menu';
let resultsForced = false;

const playerName = resolveName(store, () => `Builder ${Math.floor(Math.random() * 900 + 100)}`);

function bestKey(m: Mode): string { return `best:${m.id}`; }

function setMuted(m: boolean): void { sfx.setMuted(m); store.set('muted', m); }

const unlockOnce = () => {
  try { sfx.unlock(); } catch { /* audio is best-effort */ }
  window.removeEventListener('pointerdown', unlockOnce);
  window.removeEventListener('keydown', unlockOnce);
};
window.addEventListener('pointerdown', unlockOnce);
window.addEventListener('keydown', unlockOnce);

function teardownRound(): void {
  countdown?.cancel();
  countdown = null;
  runner?.destroy();
  runner = null;
  if (waitTimer !== null) { clearTimeout(waitTimer); waitTimer = null; }
}

/** Leave the room for real. The ONLY place net.leave() is ever called. */
async function leaveRoom(): Promise<void> {
  lobbyHandle?.destroy();
  lobbyHandle = null;
  bannerEl = null;
  rounds?.destroy();
  rounds = null;
  session?.destroy();
  session = null;
  const n = net;
  net = null;
  roomCode = '';
  seatedIds = [];
  roster = [];
  tally = {};
  clearRoomInUrl();
  if (n) { try { await n.leave(); } catch { /* already gone */ } }
}

// ------------------------------------------------------------------ menu ----
function showMenu(): void {
  screen = 'menu';
  teardownRound();
  document.body.classList.remove('playing');
  void leaveRoom();
  renderMenu({
    container: stage,
    mode,
    best: store.get(bestKey(mode), 0),
    muted: sfx.muted(),
    onMode: (m) => { mode = m; store.set('mode', m.id); showMenu(); },
    onSolo: () => startLocal(newSeed(), []),
    onRace: () => startLocal(newSeed(), [
      { id: 'bot1', name: 'Mason', bot: 'steady' as const },
      { id: 'bot2', name: 'Chip', bot: 'wide' as const },
      { id: 'bot3', name: 'Tilt', bot: 'random' as const },
    ]),
    onDaily: () => startLocal(dailySeed(), []),
    onFriends: () => showRoomEntry(),
    onHowTo: () => openModal('How to play', HOW_TO_HTML),
    onAbout: () => openModal('About Kilter', ABOUT_HTML),
    onMute: (m) => { setMuted(m); showMenu(); },
  });
}

// ------------------------------------------------------------- solo round ---
function startLocal(seed: number, opponents: { id: string; name: string; bot: 'steady' | 'wide' | 'random' | 'stacker' }[]): void {
  screen = 'playing';
  teardownRound();
  stage.innerHTML = '';
  const holder = el('div', 'stage-inner');
  stage.appendChild(holder);
  document.body.classList.add('playing');

  const begin = () => {
    countdown = null;
    runner = createRunner({
      stage: holder,
      mode,
      seed,
      selfName: 'You',
      selfId: 'self',
      reducedMotion,
      sfx,
      opponents,
      onRoundEnd: (results) => showResults(results, seed, false),
      onQuit: showMenu,
    });
  };
  countdown = runCountdown({ container: holder, onDone: begin });
}

// ---------------------------------------------------------------- results ---
function showResults(results: PlayerResult[], seed: number, multiplayer: boolean): void {
  screen = 'results';
  teardownRound();
  document.body.classList.remove('playing');
  stage.innerHTML = '';

  const me = results.find((r) => r.isSelf);
  const prevBest = store.get(bestKey(mode), 0);
  if (me && me.heightCm > prevBest) store.set(bestKey(mode), me.heightCm);

  const winner = [...results].sort((a, b) => b.score - a.score)[0];
  if (winner) tally[winner.id] = (tally[winner.id] ?? 0) + 1;

  renderResults({
    container: stage,
    results,
    mode,
    seed,
    multiplayer,
    benchmark: multiplayer ? null : benchmarkFor(mode, seed),
    best: prevBest,
    tally: multiplayer ? tally : {},
    onAgain: () => {
      if (multiplayer && rounds) { rounds.vote(); showLobby(); }
      else startLocal(newSeed(), []);
    },
    onLobby: multiplayer ? () => showLobby() : undefined,
    onMenu: showMenu,
    onShare: () => shareTower(seed),
    onFeedback: () => (window as any).feedback?.open(),
  });
}

async function shareTower(seed: number): Promise<void> {
  const url = shareLink(mode, seed);
  const text = `Kilter · ${mode.name} · try this tower`;
  try {
    if (navigator.share) { await navigator.share({ title: 'Kilter', text, url }); return; }
    await navigator.clipboard.writeText(url);
    toastOnce('Link copied');
  } catch {
    toastOnce(url);
  }
}

function toastOnce(text: string): void {
  const t = el('div', 'floating-toast');
  t.textContent = text;
  document.body.appendChild(t);
  setTimeout(() => t.remove(), 2600);
}

// ------------------------------------------------------------ multiplayer ---
function showRoomEntry(): void {
  screen = 'entry';
  teardownRound();
  stage.innerHTML = '';
  createRoomEntry({
    container: stage,
    title: 'Play with friends',
    subtitle: 'Start a new room, or enter a code to join a friend.',
    onSubmit: (code, created) => { void enterRoom(normalizeRoomCode(code), created); },
    onCancel: showMenu,
  });
}

async function enterRoom(code: string, created: boolean): Promise<void> {
  if (net) return;
  roomCode = code;
  setRoomInUrl(code);
  stage.innerHTML = '<div class="connecting"><span class="spinner"></span> Connecting…</div>';

  try { setTurnConfig(await getTurnConfig()); } catch { /* fail open, never block */ }

  const repaint = () => {
    if (screen === 'lobby') showLobby();
    else if (screen === 'results') renderMultiResults(resultsForced);
  };

  net = createNet(
    { appId: roomAppId(SLUG), roomId: code, claimHost: created },
    {
      onHostChange: repaint,
      onPeerLeave: (id) => { runner?.markLeft(id); repaint(); },
      onPeers: repaint,
    },
  );

  session = createSession(net, {
    onStatus: (id, st) => runner?.applyStatus(id, st),
    onAttack: () => runner?.receiveAttack(1),
    onResult: (id, r) => {
      remoteResults.set(id, r);
      if (myResult) renderMultiResults();
    },
  });

  rounds = createRounds({
    net,
    playerName,
    minPlayers: 2,
    roundOpts: () => roundOptsOf(mode.id),
    onRound: (info) => startNetRound(info),
    onChange: () => { if (screen === 'lobby') showLobby(); else if (screen === 'results') renderMultiResults(resultsForced); },
  });

  showLobby();
}

let bannerEl: HTMLElement | null = null;

function paintModeBanner(): void {
  if (!net || !rounds || !bannerEl) return;
  const hostMode = modeOf(hostModeId(rounds, mode.id));
  bannerEl.innerHTML = net.isHost()
    ? '<span class="lm-k">Your tower (everyone races it)</span>'
    : `<span class="lm-k">The host has chosen</span><strong>${hostMode.name}</strong>`;
  if (!net.isHost()) return;
  const row = el('div', 'mode-btns compact');
  for (const m of MODES) {
    const b = el('button', `mode-btn${m.id === mode.id ? ' on' : ''}`);
    b.type = 'button';
    b.innerHTML = `<span class="mb-name">${m.name}</span>`;
    b.addEventListener('click', () => { mode = m; store.set('mode', m.id); paintModeBanner(); });
    row.appendChild(b);
  }
  bannerEl.appendChild(row);
}

function showLobby(): void {
  if (!net || !rounds) return;
  screen = 'lobby';
  teardownRound();
  document.body.classList.remove('playing');

  const mounted = stage.querySelector('.lobby-box');
  if (lobbyHandle && bannerEl?.isConnected && mounted && mounted.childElementCount > 0) {
    paintModeBanner();
    return;
  }

  lobbyHandle?.destroy();
  stage.innerHTML = '';
  bannerEl = el('div', 'lobby-mode');
  stage.appendChild(bannerEl);
  paintModeBanner();

  const lobbyBox = el('div', 'lobby-box');
  stage.appendChild(lobbyBox);
  lobbyHandle = createLobby({
    container: lobbyBox,
    net,
    rounds,
    roomCode,
    minPlayers: 2,
    maxPlayers: MAX_PLAYERS,
    onCancel: showMenu,
  });
}

function startNetRound(info: RoundInfo): void {
  if (!net || !session) return;
  screen = 'playing';
  tallied = false;
  resultsForced = false;
  teardownRound();
  lobbyHandle?.destroy();
  lobbyHandle = null;
  bannerEl = null;
  remoteResults = new Map();
  myResult = null;
  session.reset();

  mode = modeOf((info.opts as { mode?: unknown } | undefined)?.mode);
  roster = info.players.map((p) => ({ id: p.id, name: p.name }));
  seatedIds = roster.map((r) => r.id);

  if (!info.seated) {
    screen = 'lobby';
    stage.innerHTML = '';
    const box = el('div', 'spectating');
    box.innerHTML = `<h2>Round in progress</h2><p>You're in the next one. Ready up and it starts when this round ends.</p>`;
    stage.appendChild(box);
    showLobbySoon();
    return;
  }

  stage.innerHTML = '';
  const holder = el('div', 'stage-inner');
  stage.appendChild(holder);
  document.body.classList.add('playing');

  const opponents = roster
    .filter((r) => r.id !== net!.selfId)
    .map((r) => ({ id: r.id, name: r.name }));

  countdown = runCountdown({
    container: holder,
    onDone: () => {
      countdown = null;
      runner = createRunner({
        stage: holder,
        mode,
        seed: info.seed,
        selfName: 'You',
        selfId: net!.selfId,
        reducedMotion,
        sfx,
        opponents,
        onAttack: () => session!.sendAttack(seatedIds),
        onStatus: (st) => session!.sendStatus(st),
        onSelfEnd: (r) => {
          myResult = r;
          session!.sendResult(r);
          rounds?.finish();
          renderMultiResults();
          waitTimer = window.setTimeout(() => { waitTimer = null; renderMultiResults(true); }, 25000);
        },
        onRoundEnd: () => { /* networked rounds end per-peer, see onSelfEnd */ },
        onQuit: showMenu,
      });
    },
  });
}

function showLobbySoon(): void {
  window.setTimeout(() => { if (!runner) showLobby(); }, 400);
}

function renderMultiResults(force = false): void {
  if (!myResult || !net) return;
  screen = 'results';
  if (force) resultsForced = true;
  const rows: PlayerResult[] = [myResult];
  const missing: string[] = [];
  for (const r of roster) {
    if (r.id === net.selfId) continue;
    const got = remoteResults.get(r.id);
    if (got) rows.push({ ...got, name: r.name });
    else {
      missing.push(r.name);
      rows.push({
        id: r.id, name: r.name, score: 0, heightCm: 0, heightCells: 0, drops: 0,
        settled: 0, lost: 0, won: false, isSelf: false, isBot: false,
        left: !net.peers().includes(r.id),
      });
    }
  }
  const waiting = !force && missing.length > 0
    ? `Still building: ${missing.join(', ')}. Results fill in as they finish.`
    : undefined;

  teardownRound();
  document.body.classList.remove('playing');
  stage.innerHTML = '';
  const prevBest = store.get(bestKey(mode), 0);
  if (myResult.heightCm > prevBest) store.set(bestKey(mode), myResult.heightCm);
  if (!waiting && !tallied) {
    tallied = true;
    const winner = [...rows].sort((a, b) => b.score - a.score)[0];
    if (winner) tally[winner.id] = (tally[winner.id] ?? 0) + 1;
  }
  renderResults({
    container: stage,
    results: rows,
    mode,
    seed: 0,
    multiplayer: true,
    benchmark: null,
    best: prevBest,
    tally,
    waitingText: waiting,
    onAgain: () => { rounds?.vote(); showLobby(); },
    onLobby: () => showLobby(),
    onMenu: showMenu,
    onShare: () => shareTower(0),
    onFeedback: () => (window as any).feedback?.open(),
  });
}

// ------------------------------------------------------------------- boot ---
window.addEventListener('beforeunload', () => { void net?.leave(); });

function boot(): void {
  const url = new URL(location.href);
  const roomParam = url.searchParams.get('room');
  const share = readShareParams();

  if (share.seed !== null) {
    if (share.mode) { mode = share.mode; }
    const seed = share.seed;
    clearShareParams();
    startLocal(seed, []);
    return;
  }

  if (roomParam && !deepLinkConsumed) {
    deepLinkConsumed = true;
    void enterRoom(normalizeRoomCode(roomParam), false);
    return;
  }

  showMenu();
}

boot();

// Dev/debug affordance used by the verification pass; harmless in production.
(window as unknown as Record<string, unknown>).__kilter = {
  mintCode, setHidden, MODES,
  runner: () => runner,
  net: () => net,
  rounds: () => rounds,
  screen: () => screen,
};